import { computed, ref } from 'vue';

import { useAsyncStatus } from '@peanuds/composables';

import type { SourceLoadStatus, VerificationSourceState } from './access-control.types';

interface CreateVerificationSourceOptions<TData> {
  load: (userId: string) => Promise<TData | null>;
  getRequestKey?: (userId: string) => string | null;
  sourceLabel: string;
}

export const createVerificationSource = <TData>({
  load,
  getRequestKey,
  sourceLabel,
}: CreateVerificationSourceOptions<TData>) => {
  const data = ref<TData | null>(null);
  const requestStatus = useAsyncStatus();
  const source = computed<VerificationSourceState<TData>>(() => ({
    data: data.value,
    status: requestStatus.status.value as SourceLoadStatus,
  }));

  const lastResolvedRequestKey = ref<string | null>(null);

  let activeRequestKey: string | null = null;
  let resolutionPromise: Promise<void> | null = null;
  let resolutionRequestKey: string | null = null;

  const isLoading = computed(() => requestStatus.isLoading.value);

  const reset = () => {
    data.value = null;
    requestStatus.toIdle();
    lastResolvedRequestKey.value = null;
    activeRequestKey = null;
    resolutionPromise = null;
    resolutionRequestKey = null;
  };

  const fetch = async (userId: string) => {
    const requestKey = getRequestKey ? getRequestKey(userId) : userId;

    if (!userId || !requestKey) return;

    if (requestStatus.isLoading.value && activeRequestKey === requestKey) {
      return;
    }

    activeRequestKey = requestKey;
    requestStatus.toLoading();

    try {
      const response = await load(userId);

      if (activeRequestKey !== requestKey) {
        return;
      }

      data.value = response;
      requestStatus.toLoaded();
      lastResolvedRequestKey.value = requestKey;
    } catch (error) {
      if (activeRequestKey !== requestKey) {
        return;
      }

      requestStatus.toFailed();
      lastResolvedRequestKey.value = requestKey;
      console.error(`Failed to fetch ${sourceLabel} status:`, error);
    } finally {
      if (activeRequestKey === requestKey) {
        activeRequestKey = null;
      }
    }
  };

  const ensureResolved = async (userId: string) => {
    const requestKey = getRequestKey ? getRequestKey(userId) : userId;

    if (!userId || !requestKey) {
      return;
    }

    if (
      (requestStatus.isLoaded.value || requestStatus.isFailed.value) &&
      lastResolvedRequestKey.value === requestKey
    ) {
      return;
    }

    if (resolutionPromise && resolutionRequestKey === requestKey) {
      return resolutionPromise;
    }

    resolutionRequestKey = requestKey;
    resolutionPromise = (async () => {
      await fetch(userId);
    })().finally(() => {
      if (resolutionRequestKey === requestKey) {
        resolutionPromise = null;
        resolutionRequestKey = null;
      }
    });

    return resolutionPromise;
  };

  return {
    source,
    isLoading,
    reset,
    fetch,
    ensureResolved,
  };
};
